/**
 * 제품 페이지(`/products/{code}`) 4종의 콘텐츠 데이터.
 *
 * 역할 분담 (SITEMAP_PLAN §16):
 *   `/products/{code}` — **이 제품이 무엇인가.** 랜딩 + 정본 상세. 색인 대상.
 *   `/shop/{code}`     — **이걸 산다.** 가격·옵션·수량·담기. 얇게.
 * 넣을 자리를 고를 때의 판별식: *이미 사기로 마음먹은 사람에게도 필요한가?* 아니오면 여기.
 *
 * 형식은 `/products/pls8`(8 beats)이 표준이고, 네 제품이 같은 유닛이라 화면은 한 장
 * (`ProductDetail.page.tsx`)이다 — 제품마다 다른 건 이 표뿐이다(Judd).
 *
 * 카피는 PERSONA §1 — 단정문. "이렇게 만들었다"까지만 쓰고 "왜 좋은지"는 쓰지 않는다.
 * 표시명·워드마크 규정은 PRODUCT_NAMES.md.
 */

import {
	DetailBandType,
	DetailImageType,
	SpecRowType,
} from '@components/systems/monolith/MonolithBands'

// kedem 6 — 히어로가 곧 "보드 위" 컷이라 마무리 판을 따로 두지 않는다.
// 같은 촬영분이 한 페이지에 두 번 나오면 §2("같은 사진 두 번 금지")의 취지에 어긋난다.
import kdm6Hero from '@public/images/pages/shop/kedem6/contents/kedem6-end.jpg'
import kdm6Unit from '@public/images/pages/shop/kedem6/contents/kedem6-2.jpg'
import kdm6Rear from '@public/images/pages/shop/kedem6/contents/kedem6-3.jpg'
import kdm6Cc from '@public/images/pages/shop/kedem6/contents/kedem6-dinamic-cc.jpg'
import kdm6Spec from '@public/images/monolith/kedem6-top.webp'

// kedem 4
import kdm4Hero from '@public/images/pages/shop/kedem4/contents/kedem4-end.jpg'
import kdm4Unit from '@public/images/pages/shop/kedem4/contents/kedem4-2.jpg'
import kdm4Rear from '@public/images/pages/shop/kedem4/contents/kedem4-3.jpg'
import kdm4Cc from '@public/images/pages/shop/kedem4/contents/kedem4-dinamic-cc.jpg'
import kdm4Spec from '@public/images/monolith/kedem4-top.webp'

// airmidi pog 2
import amp2Hero from '@public/images/pages/shop/amp2/gallery/amp2-module-mono.jpg'
import amp2Module from '@public/images/pages/shop/amp2/gallery/amp2-gallery2.jpg'
import amp2Channel from '@public/images/pages/shop/amp2/contents/channel.png'
import amp2Inst1 from '@public/images/pages/shop/amp2/contents/install/inst1.jpg'
import amp2Inst2 from '@public/images/pages/shop/amp2/contents/install/inst2.jpg'
import amp2Inst3 from '@public/images/pages/shop/amp2/contents/install/inst3.jpg'
import amp2Inst4 from '@public/images/pages/shop/amp2/contents/install/inst4.jpg'
import amp2Inst5 from '@public/images/pages/shop/amp2/contents/install/inst5.jpg'
import amp2Inst6 from '@public/images/pages/shop/amp2/contents/install/inst6.jpg'
import amp2Inst7 from '@public/images/pages/shop/amp2/contents/install/inst7.jpg'
import amp2Inst8 from '@public/images/pages/shop/amp2/contents/install/inst8.jpg'
import amp2Inst9 from '@public/images/pages/shop/amp2/contents/install/inst9.jpg'
import amp2Inst10 from '@public/images/pages/shop/amp2/contents/install/inst10.jpg'
import amp2Inst11 from '@public/images/pages/shop/amp2/contents/install/inst11.jpg'
import amp2Inst12 from '@public/images/pages/shop/amp2/contents/install/inst12.jpg'
import amp2Inst13 from '@public/images/pages/shop/amp2/contents/install/inst13.jpg'
import amp2Inst14 from '@public/images/pages/shop/amp2/contents/install/inst14.jpg'
import amp2Inst15 from '@public/images/pages/shop/amp2/contents/install/inst15.jpg'
import amp2Inst16 from '@public/images/pages/shop/amp2/contents/install/inst16.jpg'
import amp2Inst17 from '@public/images/pages/shop/amp2/contents/install/inst17.jpg'

// dual switch — contents 폴더가 비어 있어 갤러리 컷으로만 짠다 (SITEMAP_PLAN §16 보고)
import dsHero from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery2.jpg'
import dsClose from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery3.jpg'

/**
 * 단종 표기 (2026-09-16). 히어로의 리드 아래에 판으로 선다.
 * 카피는 PERSONA §1 단정문 — 사실만 쓰고 "성공적으로" 류 형용사를 쓰지 않는다.
 */
export interface ProductStatusType {
	/** 키커 급 라벨 */
	label: string
	/** 사실 문장들. 마지막 줄은 쓰던 사람에게 남는 것 */
	lines: string[]
}

export interface ProductDetailType {
	code: string
	name: string
	wordmark: 'koinos' | 'kedem' | 'text'
	model?: string
	/** 단종된 제품이면 히어로에 상태 판이 붙는다 */
	status?: ProductStatusType
	/** 히어로 워드마크 아래 한 줄. 단정문 */
	tagline: string
	/** 태그라인 아래 한 문단. 이 제품이 무엇인지까지만 */
	lede: string
	/** 히어로 컷 */
	hero: DetailImageType
	bands: DetailBandType[]
	/** 클로즈 밴드의 한 줄. 그 아래 블랙 CTA 하나 */
	closeHeading: string
}

const KEDEM_WORKS_WITH: SpecRowType[] = [
	{
		label: 'Amp modellers',
		value:
			'Neural DSP Quad Cortex, Kemper Profiler and Profiler Player, Line 6 Helix, Fractal Audio',
	},
	{
		label: 'Delay and reverb',
		value: 'Strymon Timeline, BigSky and Volante; Eventide H9 and H90; Meris',
	},
	{
		label: 'Pedals with TRS MIDI',
		value: 'Chase Bliss, Meris, Empress, Source Audio',
	},
	{
		label: 'Amps',
		value: 'Channel switching through the TRS MIDI/Relay output',
	},
]

/** 설치 17단계 — 구 페이지 `installContents` 의 문장을 그대로 옮겼다 */
const AMP2_INSTALL: DetailImageType[] = [
	{
		src: amp2Inst1,
		alt: 'AirMIDI POG 2 module connectors',
		caption:
			'01 · The module has three connectors: from the top, a 6-pin RTS_CON, a 2-pin SW_CON, and a 6-pin PRE_CON.',
	},
	{
		src: amp2Inst2,
		alt: 'RTS_CON solder points on the POG2 board',
		caption: '02 · Where RTS_CON solders to the POG2.',
	},
	{
		src: amp2Inst3,
		alt: 'RTS_CON pins soldered',
		caption:
			'03 · Solder the five RTS_CON pins near the top-left corner of the POG2 PCB, following the colours and connector order.',
	},
	{
		src: amp2Inst4,
		alt: 'RTS_LED wire at CN2A',
		caption: '04 · The red RTS_LED wire goes to CN2A, the LED point.',
	},
	{
		src: amp2Inst5,
		alt: 'RTS_CON connected',
		caption:
			'05 · Connect the connector to RTS_CON. Check each cable against its name before connecting.',
	},
	{
		src: amp2Inst6,
		alt: 'Engage footswitch removed',
		caption:
			'06 · Loosen the Engage footswitch and melt the solder at the points shown to free the cables. It is the left-hand switch from the PCB side.',
	},
	{
		src: amp2Inst7,
		alt: 'Engage switch cable',
		caption: '07 · The Engage switch cable with the switch removed.',
	},
	{
		src: amp2Inst8,
		alt: 'Cable ends taped',
		caption:
			'08 · Tape and seal the cable ends. No pin may touch another, or the pedal misbehaves.',
	},
	{
		src: amp2Inst9,
		alt: 'Supplied switch fitted',
		caption: '09 · Fit the supplied switch and secure it with the nut.',
	},
	{
		src: amp2Inst10,
		alt: 'SW_CON connected',
		caption: '10 · Run the switch cable to SW_CON. Black and red order does not matter.',
	},
	{
		src: amp2Inst11,
		alt: 'PRE_CON solder points',
		caption:
			'11 · Solder the six PRE_CON wires to the bottom-left area of the POG2 PCB, each to its colour.',
	},
	{
		src: amp2Inst12,
		alt: 'PRE_CON connected',
		caption:
			'12 · Connect the soldered cables to PRE_CON. Check each against its number and colour first.',
	},
	{
		src: amp2Inst13,
		alt: 'Centre screw inserted',
		caption: '13 · Insert the supplied screw into the hole at the centre of the board.',
	},
	{
		src: amp2Inst14,
		alt: 'White support attached',
		caption: '14 · Attach the white support to the back side.',
	},
	{
		src: amp2Inst15,
		alt: 'POG2 centre screw loosened',
		caption: '15 · Loosen the screw at the very centre of the POG2 PCB.',
	},
	{
		src: amp2Inst16,
		alt: 'AirMIDI screw fastened',
		caption: '16 · Fasten the AirMIDI screw where the POG2 centre screw was.',
	},
	{
		src: amp2Inst17,
		alt: 'Antenna cable soldered to the enclosure',
		caption:
			'17 · Solder the antenna cable, circled, to the aluminium enclosure. The enclosure becomes the antenna. Paint blocks it.',
	},
]

export const productDetailList: ProductDetailType[] = [
	{
		code: 'KDM6',
		name: 'Kedem 6',
		wordmark: 'kedem',
		model: '6',
		status: {
			label: 'Discontinued',
			lines: [
				'The first generation is out of production. Every unit we built has an owner.',
				'We still support them: manuals, firmware, and repair.',
			],
		},
		tagline: 'Six switches. One screen.',
		lede: 'A MIDI foot controller with the settings on the unit. Six switches, a 3.5-inch touch display, AirMIDI wireless. There is no app step.',
		hero: { src: kdm6Hero, alt: 'Kedem 6' },
		bands: [
			{
				id: 'unit',
				kicker: 'The unit',
				rows: [
					{
						label: 'Touch display',
						value: 'Every setting is on the 3.5-inch screen. No app, no computer.',
					},
					{
						label: 'Switch actions',
						value:
							'One press, double press, long press, double long press, and a release action for each. Eight per switch. Two switches combine.',
					},
					{
						label: 'Banks',
						value:
							'100. Six switches and 15 switch combinations per bank, 30 MIDI messages per preset.',
					},
					{
						label: 'Expression',
						value:
							'The zero point is set on the unit. Up to 5 MIDI CC messages per switch.',
					},
				],
				images: [{ src: kdm6Unit, alt: 'Kedem 6 touch display' }],
			},
			{
				id: 'dynamic-cc',
				kicker: 'Dynamic CC',
				lede: 'A CC value moves over 0.1 to 10 seconds. Volume swells and filter sweeps arrive as a ramp, not a step.',
				images: [{ src: kdm6Cc, alt: 'Dynamic CC on the Kedem display' }],
			},
			{
				id: 'rear',
				kicker: 'Rear panel',
				lede: 'Two MIDI OUT ports, two TRS MIDI ports and USB MIDI reach several devices at once. No hub in the chain.',
				rows: [
					{ label: 'MIDI', value: '1× MIDI IN, 2× MIDI OUT, 1× USB MIDI OUT' },
					{ label: 'TRS', value: '2× TRS MIDI, 2× TRS MIDI/Relay' },
					{ label: 'External', value: '2× switch inputs, 1× expression pedal input' },
					{ label: 'Power', value: '9V DC, centre negative. 250 mA' },
				],
				images: [{ src: kdm6Rear, alt: 'Kedem 6 rear panel ports' }],
			},
			{
				id: 'works-with',
				kicker: 'Works with',
				lede: 'Kedem 6 speaks standard MIDI. It controls anything that accepts Program Change or Control Change.',
				rows: KEDEM_WORKS_WITH,
				note: 'AirMIDI carries the same messages without a cable, to Judahs devices that speak it.',
			},
			{
				id: 'specifications',
				kicker: 'Specifications',
				rows: [
					{ label: 'Dimensions', value: '190 mm (W) × 121 mm (D) × 62 mm (H)' },
					{ label: 'Weight', value: '960 g (2.12 lbs)' },
					{ label: 'Display', value: '3.5-inch touch LCD' },
					{ label: 'Power', value: '9V DC, centre negative. 250 mA' },
					{
						label: 'MIDI ports',
						value:
							'1× MIDI IN, 2× MIDI OUT, 1× USB MIDI OUT, 2× TRS MIDI, 2× TRS MIDI/Relay',
					},
					{
						label: 'External controls',
						value: '2× external switch inputs, 1× expression pedal input',
					},
					{ label: 'Wireless MIDI', value: 'AirMIDI' },
					{
						label: 'Storage',
						value:
							'100 banks. 6 switches and 15 switch combinations per bank, 8 actions each, 30 MIDI messages per preset',
					},
				],
				images: [{ src: kdm6Spec, alt: 'Kedem 6' }],
			},
			{
				id: 'kedem-4',
				kicker: 'Kedem 6 or 4',
				link: { label: 'Kedem 4', to: '/products/kdm4' },
				lede: 'Same display, same AirMIDI, same editor. The difference is width, and how many things you reach at once.',
				rows: [
					{
						label: 'Kedem 6',
						value:
							'190 mm, 960 g. Six switches, 2× MIDI OUT, 2× TRS MIDI, 2× external switch inputs.',
					},
					{
						label: 'Kedem 4',
						value: '140 mm, 760 g. Four switches, one of each port.',
					},
				],
			},
		],
		closeHeading: 'Six switches, and the screen that sets them.',
	},

	{
		code: 'KDM4',
		name: 'Kedem 4',
		wordmark: 'kedem',
		model: '4',
		status: {
			label: 'Discontinued',
			lines: [
				'The first generation is out of production. Every unit we built has an owner.',
				'We still support them: manuals, firmware, and repair.',
			],
		},
		tagline: 'Four switches. Same screen.',
		lede: 'The same display, the same AirMIDI, the same editor as the Kedem 6. Four switches across 140 mm, for the board that has run out of room.',
		hero: { src: kdm4Hero, alt: 'Kedem 4' },
		bands: [
			{
				id: 'unit',
				kicker: 'The unit',
				rows: [
					{
						label: 'Touch display',
						value: 'Every setting is on the 3.5-inch screen. No app, no computer.',
					},
					{
						label: 'Switch actions',
						value:
							'One press, double press, long press, double long press, and a release action for each. Eight per switch.',
					},
					{
						label: 'Banks',
						value:
							'100. Four switches and their combinations per bank, 30 MIDI messages per preset.',
					},
					{
						label: 'Expression',
						value:
							'The zero point is set on the unit. Up to 5 MIDI CC messages per switch.',
					},
				],
				images: [{ src: kdm4Unit, alt: 'Kedem 4 touch display' }],
			},
			{
				id: 'dynamic-cc',
				kicker: 'Dynamic CC',
				lede: 'A CC value moves over 0.1 to 10 seconds. Volume swells and filter sweeps arrive as a ramp, not a step.',
				images: [{ src: kdm4Cc, alt: 'Dynamic CC on the Kedem display' }],
			},
			{
				id: 'rear',
				kicker: 'Rear panel',
				lede: 'One of each port, in 140 mm. USB MIDI and TRS MIDI/Relay are both here.',
				rows: [
					{ label: 'MIDI', value: '1× MIDI IN, 1× MIDI OUT, 1× USB MIDI OUT' },
					{ label: 'TRS', value: '1× TRS MIDI, 1× TRS MIDI/Relay' },
					{ label: 'External', value: '1× switch input, 1× expression pedal input' },
					{ label: 'Power', value: '9V DC, centre negative. 250 mA' },
				],
				images: [{ src: kdm4Rear, alt: 'Kedem 4 rear panel ports' }],
			},
			{
				id: 'works-with',
				kicker: 'Works with',
				lede: 'Kedem 4 speaks standard MIDI. It controls anything that accepts Program Change or Control Change.',
				rows: KEDEM_WORKS_WITH,
				note: 'AirMIDI carries the same messages without a cable, to Judahs devices that speak it.',
			},
			{
				id: 'specifications',
				kicker: 'Specifications',
				rows: [
					{ label: 'Dimensions', value: '140 mm (W) × 121 mm (D) × 62 mm (H)' },
					{ label: 'Weight', value: '760 g (1.68 lbs)' },
					{ label: 'Display', value: '3.5-inch touch LCD' },
					{ label: 'Power', value: '9V DC, centre negative. 250 mA' },
					{
						label: 'MIDI ports',
						value:
							'1× MIDI IN, 1× MIDI OUT, 1× USB MIDI OUT, 1× TRS MIDI, 1× TRS MIDI/Relay',
					},
					{
						label: 'External controls',
						value: '1× external switch input, 1× expression pedal input',
					},
					{ label: 'Wireless MIDI', value: 'AirMIDI' },
					{
						label: 'Storage',
						value:
							'100 banks. 4 switches and their combinations per bank, 8 actions each, 30 MIDI messages per preset',
					},
				],
				images: [{ src: kdm4Spec, alt: 'Kedem 4' }],
			},
			{
				id: 'kedem-6',
				kicker: 'Kedem 4 or 6',
				link: { label: 'Kedem 6', to: '/products/kdm6' },
				lede: 'Same display, same AirMIDI, same editor. The difference is width, and how many things you reach at once.',
				rows: [
					{
						label: 'Kedem 4',
						value: '140 mm, 760 g. Four switches, one of each port.',
					},
					{
						label: 'Kedem 6',
						value:
							'190 mm, 960 g. Six switches, 2× MIDI OUT, 2× TRS MIDI, 2× external switch inputs.',
					},
				],
			},
		],
		closeHeading: 'The same screen, in 140 mm.',
	},

	{
		code: 'AMP2',
		name: 'AirMIDI POG 2',
		wordmark: 'text',
		tagline: 'The POG2, over the air.',
		lede: 'A module that goes inside an Electro-Harmonix POG2 and gives it wireless MIDI. Preset switching and bypass from any Judahs controller. No cable to the pedal, no pairing.',
		hero: { src: amp2Hero, alt: 'AirMIDI POG 2' },
		bands: [
			{
				id: 'airmidi',
				kicker: 'Against a wired mod',
				lede: 'The other ways to put MIDI in a POG2 are wired: a module inside the pedal, and a cable from the controller to it. AirMIDI does the same job over the air.',
				rows: [
					{ label: 'Cable', value: 'None between the controller and the POG2' },
					{
						label: 'Pairing',
						value: 'None. Each device carries a registered AirMIDI code',
					},
					{
						label: 'Presets',
						value: 'Switched from any Judahs controller that speaks AirMIDI',
					},
					{ label: 'Link', value: 'ESP-NOW' },
				],
				images: [{ src: amp2Module, alt: 'AirMIDI POG 2 module' }],
			},
			{
				id: 'midi-cc',
				kicker: 'MIDI CC',
				rows: [
					{ label: 'Bypass', value: 'CC 24. 0 bypassed, 127 engaged' },
					{ label: 'Preset up', value: 'CC 96. Any value increments' },
					{ label: 'Preset down', value: 'CC 97. Any value decrements' },
				],
			},
			{
				id: 'channels',
				kicker: 'Channels',
				lede: 'Set the DIP switches to the channel you want. The POG2 shows it top right as CH 1, CH 2, and so on.',
				// 16채널 × DIP 4개가 든 표다. 반칸으로 줄이면 숫자가 안 읽힌다
				wideImage: true,
				images: [{ src: amp2Channel, alt: 'AirMIDI POG 2 DIP switch channel map' }],
			},
			{
				id: 'fitting',
				kicker: 'Fitting',
				lede: 'Seventeen steps with a soldering iron.',
				note: 'We fit it if you choose the installation option at checkout. You send the pedal to us, and shipping applies. Orders under $200 carry a partial shipping fee under the store policy.',
				images: AMP2_INSTALL,
			},
			{
				id: 'specifications',
				kicker: 'Specifications',
				rows: [
					{ label: 'Fits', value: 'Electro-Harmonix POG2' },
					{
						label: 'Link',
						value: 'AirMIDI, on ESP-NOW. No pairing; each unit carries a code',
					},
					{
						label: 'Control',
						value: 'Preset switching and bypass from any Judahs AirMIDI controller',
					},
					{
						label: 'Channels',
						value: 'Set on DIP switches. The channel shows top right on the POG2',
					},
					{
						label: 'Antenna',
						value: 'The POG2 aluminium enclosure, soldered to the module',
					},
					{
						label: 'Fitting',
						value: 'Soldered inside the pedal. Installation service available',
					},
				],
			},
		],
		closeHeading: 'The POG2 you already own, without the cable.',
	},

	{
		code: 'DS',
		name: 'Dual Switch',
		wordmark: 'text',
		tagline: 'Two switches. One TRS cable.',
		lede: 'Two momentary footswitches on a single TRS cable. It plugs into the external switch input of a Judahs controller and adds two more switches to the board.',
		hero: { src: dsHero, alt: 'Judahs Dual Switch' },
		bands: [
			{
				id: 'what-it-does',
				kicker: 'What it does',
				lede: 'One TRS cable adds two switches to a controller that has run out of them.',
				rows: [
					{
						label: 'Kedem 6',
						value: 'Two external switch inputs. Two Dual Switches take the count to ten.',
					},
					{
						label: 'Kedem 4',
						value: 'One external switch input. One Dual Switch takes the count to six.',
					},
					{
						label: 'Assignment',
						value:
							'Each switch is set on the controller, with the same eight actions as a switch on the unit.',
					},
				],
			},
			{
				id: 'specifications',
				kicker: 'Specifications',
				rows: [
					{ label: 'Switches', value: '2× momentary' },
					{ label: 'Connection', value: '1/4-inch TRS input jack' },
					{
						label: 'Works with',
						value: 'Judahs products with a TRS external switch input',
					},
					{ label: 'Build', value: 'Handmade at Judahs' },
				],
			},
			{
				kicker: 'On the board',
				images: [{ src: dsClose, alt: 'Judahs Dual Switch' }],
			},
		],
		closeHeading: 'Two more switches, on the cable you already run.',
	},
]

/** 코드(대문자)로 찾는다. 없는 코드면 undefined — 라우트가 4종만 걸어 두어 실제로는 나지 않는다. */
export const getProductDetail = (code: string): ProductDetailType | undefined =>
	productDetailList.find(aDetail => aDetail.code === code.toUpperCase())
